import { Component, Input, NgZone, OnDestroy, OnInit, Output } from "@angular/core";
import {
    getOfferListFromStorage,
    WebshopUtils,
} from 'src/app/shared/webshop/webshop-utils';

import { AnalyticsService, LanguageService, TranslationService } from '@proximus/ng-common';
import {
    OptionDetailsBackgroundService,
    OptionParams,
} from '../option-details-background.service';
import { OrderButtonComponent } from 'src/app/shared/order-button/order-button.component';
import { OptionLockService } from 'src/app/services/option-lock.service';
import { OptionConfigurationLockService } from 'src/app/services/option-configuration-lock.service';
import { Alert, AlertsService, ModalService, PxModalMessageService } from '@proximus/ng-common-components';
import { ApplicationState, getDisneyPlusToken, getDisneyPlusTokenError } from 'src/app/store/reducers';
import { Store } from '@ngrx/store';
import { getMprdProductType, isOptionAvailable } from '@dch/products-business-logic';
import { Subscription as RxJSSubscription } from 'rxjs';
import { GetDisneyWebToken } from 'src/app/store/actions/disney-plus-account-management.actions';
import { getPiiServiceId } from 'src/app/shared/pii/pii.utils';
import { isStpOption, onResult } from 'src/app/shared/order-utils/order-utils';
import { isIffOption } from 'src/app/shared/order-utils/order-utils';
import { DISNEY_PLUS_PREMIUM, DISNEY_PLUS_STANDARD } from 'src/app/details/constants';
import { inWebView } from 'src/app/shared/browser-utils/browser-utils';

const PRODUCT_TYPE = 'RPO';

export enum OrderButtonType {
    ACTIVE = 'active',
    ORDER_STANDARD = 'order_standard',
    ORDER_PREMIUM = 'order_premium',
    UPGRADE = 'upgrade',
    DOWNGRADE = 'downgrade',
    ACTIVATE = 'activate',
    WATCH = 'watch',
    CEASE = 'cease',
    PACK_ORDER = 'pack_order',
    PACK_ORDER_PREMIUM = 'pack_order_premium',
    PACK_UPGRADE_PREMIUM = 'pack_upgrade_premium',
    PACK_DOWNGRADE = 'pack_downgrade',
}

@Component({
    selector: 'mprd-disney-plus-order-button',
    templateUrl: './disney-plus-order-button.component.html',
})
export class DisneyPlusOrderButtonComponent
    extends OrderButtonComponent
    implements OnInit, OnDestroy {
    public ButtonType = OrderButtonType;

    @Input() buttonType: OrderButtonType;
    @Input() buttonClass: string;

    private registerOptionParamsExecutedSubscription: RxJSSubscription;
    private buttonStateChangeExecutedSubscription: RxJSSubscription;

    public service: OptionDetailsBackgroundService;

    private queryParamToken: string;
    public isLoadingToken: boolean;

    constructor(
        protected modalService: ModalService,
        store: Store<ApplicationState>,
        ngZone: NgZone,
        analyticsService: AnalyticsService,
        optionLockService: OptionLockService,
        optionConfigurationLockService: OptionConfigurationLockService,
        translationService: TranslationService,
        protected pxModalMessageService: PxModalMessageService,
        protected languageService: LanguageService,
        private optionDetailsBackgroundService: OptionDetailsBackgroundService,
        private alertsService: AlertsService,
    ) {
        super(
            modalService,
            store,
            ngZone,
            analyticsService,
            optionLockService,
            optionConfigurationLockService,
            translationService
        );

        this.service = optionDetailsBackgroundService;

        // Guarantees that the option params are set here from OptionDetailsBackgroundService whithout using inputs
        this.registerOptionParamsExecutedSubscription =
            this.service.registerOptionParamsExecutedSubject.subscribe(() => {
                this.initializeOrderButton(this.service.getOptionParams());
            });

        this.buttonStateChangeExecutedSubscription =
            this.buttonStateChangeExecutedSubject.subscribe(() => {
                this.service.setLoadingState(this.isLoadingState);

                if (
                    isStpOption(this.option, this.isSwap) ||
                    this.isSwap ||
                    isIffOption(this.option)
                ) {
                    // Disable all buttons if any of the following conditions below are true
                    this.service.setAllButtonsDisabled(this.orderDisabled || this.isPendingInCart || this.pendingOrder);
                }
            });
    }

    ngOnInit(): void {
        this.initializeOrderButton(this.service.getOptionParams());
        this.initializeDisneyPlusToken();
    }

    ngOnDestroy(): void {
        this.registerOptionParamsExecutedSubscription.unsubscribe();
        this.buttonStateChangeExecutedSubscription.unsubscribe();
    }

    public getButtonParams() {
        const genericClasses = [];

        if (this.service.isLoadingState) {
            genericClasses.push('rs-btn-waiting');
        }

        if (this.service.isDisabledState) {
            genericClasses.push('rs-btn-disabled');
        }

        return {
            [OrderButtonType.ORDER_STANDARD]: {
                labelKey: 'option.details.disneyplus.standard.order',
                class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.ORDER_PREMIUM]: {
                labelKey: 'option.details.disneyplus.premium.order',
                class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.UPGRADE]: {
                labelKey: 'option.details.disneyplus.upgrade',
                class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.DOWNGRADE]: {
                labelKey: 'option.details.disneyplus.downgrade',
                class: ['rs-btn', 'rs-btn-third', 'rs-btn-neg', 'rs-maxw'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                showRightIcon: !this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.CEASE]: {
                labelKey: 'option.details.cease',
                class: ['rs-btn', 'rs-btn-second', 'rs-btn-neg'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.PACK_ORDER]: {
                labelKey: 'option.details.order',
                class: ['rs-btn', 'rs-btn-order', 'rs-maxw'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.PACK_ORDER_PREMIUM]: {
                labelKey: 'tv.active.options.disneyplus.orderForPicks',
                class: ['rs-btn', 'rs-btn-order'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.PACK_UPGRADE_PREMIUM]: {
                labelKey: 'tv.active.options.disneyplus.UpgradeForPicks',
                class: ['rs-btn', 'rs-btn-order'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
            [OrderButtonType.PACK_DOWNGRADE]: {
                labelKey: 'tv.active.options.disneyplus.DowngradeForPicks',
                class: ['rs-btn', 'rs-btn-third', 'rs-btn-neg', 'rs-btn-nl-so'].concat(genericClasses),
                disabled: this.service.isLoadingState,
                showRightIcon: !this.service.isLoadingState,
                click: () => this.executeDisneyPlusOrder(),
            },
        }[this.buttonType];
    }

    private initializeOrderButton(optionParams: OptionParams) {
        this.setOptionParams(optionParams);
        this.internalOnChanges();
        this.setupResultEmitSubscription();
    }

    private setOptionParams(optionParams: OptionParams) {
        const {
            option,
            selectedProduct,
            isDeepLink,
            deepLinkStatus,
            treatmentCode,
            queryParamToken
        } = optionParams;

        this.option = option;
        this.selectedProduct = selectedProduct;
        this.isDeepLink = isDeepLink;
        this.deepLinkStatus = deepLinkStatus;
        this.treatmentCode = treatmentCode;
        this.queryParamToken = queryParamToken;
    }

    private setupResultEmitSubscription() {
        // Used to show the confirmation or warning messages
        this.resultEmit.subscribe((event) => {
            const isTvProduct = getMprdProductType(this.selectedProduct) === 'tv';

            onResult(
                event,
                undefined,
                this.selectedProduct,
                this.option,
                this.translationService,
                this.languageService,
                this.modalService,
                this.pxModalMessageService,
                this.alertsService,
                this.treatmentCode,
                isTvProduct,
                false
            );
        });
    }

    public executeDisneyPlusOrder() {
        this.loading = true;
        this.resultEmit.emit({ orderingInProgress: true });

        this.calculateLoadingState();

        const isTryAndBuyOptInOrOptOut =
            this.option &&
            this.option.trialOption &&
            this.option.trialOption.endOfTrialDecision !== undefined;

        if (isTryAndBuyOptInOrOptOut) {
            this.executeTrialFlow();
        } else {
            this.executeTaggingEventFlow();

            const payload = {
                action: this.getOrderAction(),
                isReverse: false,
                isPremium: this.getStandardOrPremium(),
                product: {
                    mpcUuid: this.getDPMpcUuid(),
                    type: PRODUCT_TYPE,
                    chargeEvent: this.getChargeEvent(),
                    chargeCode: this.getChargeCode(),
                    promoCode: undefined,
                },
            };

            WebshopUtils.addProduct(
                payload.isReverse,
                payload.product.mpcUuid,
                payload.product.type,
                payload.product.chargeEvent,
                payload.product.chargeCode,
                payload.action,
                getOfferListFromStorage(this.treatmentCode),
                payload.product.promoCode,
                payload.isPremium,
            );
        }
    }

    private getOrderAction(): 'add' | 'remove' {
        const addButtonTypes = [
            OrderButtonType.ORDER_STANDARD,
            OrderButtonType.ORDER_PREMIUM,
            OrderButtonType.UPGRADE,
            OrderButtonType.PACK_ORDER,
            OrderButtonType.PACK_ORDER_PREMIUM,
            OrderButtonType.PACK_UPGRADE_PREMIUM,
        ];

        if (addButtonTypes.includes(this.buttonType)) {
            return 'add';
        }

        const removeButtonTypes = [
            OrderButtonType.CEASE,
            OrderButtonType.DOWNGRADE,
            OrderButtonType.PACK_DOWNGRADE,
        ];

        if (removeButtonTypes.includes(this.buttonType)) {
            return 'remove';
        }

        return isOptionAvailable(this.option) || !this.option.ibStatus
            ? 'add'
            : 'remove';
    }

    private getStandardOrPremium(): boolean {
        const premiumButtonTypes = [
            OrderButtonType.ORDER_PREMIUM,
            OrderButtonType.PACK_ORDER_PREMIUM,
        ];
        if (premiumButtonTypes.includes(this.buttonType)) {
            return true;
        }
        return false;
    }

    private getDPMpcUuid() {
        const standardButtonTypes = [
            OrderButtonType.ORDER_STANDARD,
            OrderButtonType.ORDER_PREMIUM,
        ];

        const premiumButtonTypes = [
            OrderButtonType.UPGRADE,
            OrderButtonType.DOWNGRADE,
            OrderButtonType.PACK_UPGRADE_PREMIUM,
            OrderButtonType.PACK_DOWNGRADE,
        ];

        if (standardButtonTypes.includes(this.buttonType)) {
            return DISNEY_PLUS_STANDARD;
        }

        if (premiumButtonTypes.includes(this.buttonType)) {
            return DISNEY_PLUS_PREMIUM;
        }

        return this.option.optionCodeForDescription || this.option.mpcUuid;
    }

    public activateDisneyPlus() {
        this.isLoadingToken = true;
        this.store.dispatch(
            new GetDisneyWebToken({
                serviceId: getPiiServiceId(),
                token: this.queryParamToken,
            }),
        );
    }

    private initializeDisneyPlusToken() {
        this.addSubscription(
            this.store.select(getDisneyPlusToken).subscribe((value) => {
                if (value && value.token && this.isLoadingToken) {
                    const disneyPlusUrl = this.translationService
                        .getTranslatedValueFor(
                            'tv.active.options.disneyplus.button.activate.link',
                        )
                        .replace('{0}', value.token);
                    this.goToDisneyPlusActivationPage(disneyPlusUrl);
                    this.isLoadingToken = false;
                }
            }),
        );

        this.addSubscription(
            this.store.select(getDisneyPlusTokenError).subscribe((error) => {
                if (error) {
                    const alert: Alert = {
                        isClosable: true,
                        alertKey: 'disneyPlusActivationAlert',
                        title: this.translationService.getTranslatedValueFor(
                            'tv.active.options.disneyplus.error.title',
                        ),
                        description: this.translationService.getTranslatedValueFor(
                            'tv.active.options.disneyplus.error.description',
                        ),
                        type: 'error',
                        useNegativeColors: true,
                    };
                    this.alertsService.dispatchAlert(alert);
                    this.isLoadingToken = false;
                }
            }),
        );
    }

    private goToDisneyPlusActivationPage(disneyPlusUrl: string) {
        const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
        const isSafari = !isChrome && navigator.userAgent.indexOf('Safari') > -1;
        const inWebViewFeatureFlag = this.translationService.getTranslatedValueFor(
            'tv.disneyplus.activate.inWebViewFeatureFlag');
        if (inWebViewFeatureFlag && inWebView()) {
            window.location.href = disneyPlusUrl;
        } else {
            window.open(disneyPlusUrl, isSafari ? '_top' : '_blank');
        }
    }
}
